import React, { useState } from 'react';
import { Brain, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { analyzeDeepDomain } from '@/lib/phishingLab';
import { useApp } from '@/context/AppContext';
import { RiskGraph } from './SharedComponents';

const exampleUrls = [
  { url: 'https://google.com', label: 'Legit', safe: true },
  { url: 'https://g00gle-login.security-verify.xyz/account', label: 'Multi-vector', safe: false },
  { url: 'https://apple.verify.account-service.tk/login', label: 'Subdomain', safe: false },
];

export const DefenderMode = () => {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addVerification } = useApp();

  const handleAnalyze = async () => {
    if (!url.trim()) return;
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 600));
    const analysis = analyzeDeepDomain(url);
    setResult(analysis);

    addVerification({
      type: 'phishing-lab',
      input: `Defender · ${url.substring(0, 80)}${url.length > 80 ? '…' : ''}`,
      verdict: 'Lab Analysis',
      score: analysis?.riskScore || 0,
      result: {
        mode: 'defender',
        url,
        riskScore: analysis?.riskScore || 0,
      },
    });

    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <GlassCard className="p-4 sm:p-5">
        <div className="space-y-3">
          <div className="relative">
            <Input type="text" placeholder="Paste a URL to analyze..." value={url}
              onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
              className="h-12 pl-10 text-sm" data-testid="defender-url-input" />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
          <div className="flex flex-wrap gap-2">
            {exampleUrls.map((ex, i) => (
              <button key={i} onClick={() => setUrl(ex.url)}
                className={cn("px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors",
                  ex.safe ? "bg-success/10 hover:bg-success/20 text-success" : "bg-destructive/10 hover:bg-destructive/20 text-destructive")}>
                {ex.label}
              </button>
            ))}
          </div>
          <Button onClick={handleAnalyze} disabled={!url.trim() || isLoading} variant="gradient"
            className="w-full sm:w-auto h-11" data-testid="defender-analyze-btn">
            {isLoading ? <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              : <><Brain className="w-4 h-4 mr-2" />Deep Domain Analysis</>}
          </Button>
        </div>
      </GlassCard>

      {result && (
        <div className="space-y-4">
          <GlassCard className={cn("p-4 sm:p-5 border-2",
            result.riskScore >= 60 ? 'border-destructive/30' : result.riskScore >= 30 ? 'border-warning/30' : 'border-success/30')}>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className={cn("w-20 h-20 rounded-full flex items-center justify-center border-4",
                result.riskScore >= 60 ? 'border-destructive/50 bg-destructive/10' :
                result.riskScore >= 30 ? 'border-warning/50 bg-warning/10' : 'border-success/50 bg-success/10')}>
                <span className={cn("text-2xl font-bold font-display",
                  result.riskScore >= 60 ? 'text-destructive' : result.riskScore >= 30 ? 'text-warning' : 'text-success')}>
                  {result.riskScore}
                </span>
              </div>
              <div className="text-center sm:text-left">
                <Badge variant={result.riskScore >= 60 ? 'danger' : result.riskScore >= 30 ? 'suspicious' : 'safe'} className="text-sm mb-1">
                  {result.verdict}
                </Badge>
                <p className="text-xs text-muted-foreground font-mono break-all">{result.hostname}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {result.signals.filter(s => s.score > 0).length} risk signals across {result.signals.length} categories
                </p>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-4 sm:p-5">
            <h4 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Brain className="w-4 h-4 text-primary" />
              Explainable Risk Graph
              <span className="text-[10px] text-muted-foreground font-normal">click nodes to expand</span>
            </h4>
            <div className="space-y-1" data-testid="risk-graph">
              <RiskGraph signals={result.signals} />
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
};
